import { useCallback, useEffect, useState } from "react";

import api from "../api/client";

/**
 * Per-unit rates for a contract — "$X per managed endpoint / per seat".
 *
 * The preview is not a nicety. These rates bill against a LIVE fleet count, so
 * without showing the count and the arithmetic first, generating the invoice is
 * the moment the operator finds out what they charged. It also surfaces the two
 * things the server refuses to absorb silently:
 *
 *   - devices matching no rate row (an MSP pricing workstations and servers
 *     leaves role="unknown" machines unbilled — a revenue hole)
 *   - devices that have not checked in for a month (billed on purpose, because
 *     a quiet machine is often still managed, but worth a look first)
 *
 * The double-billing guard (endpoint + workstation/server) is enforced
 * server-side; mirrored here only so the operator sees it before saving.
 */

type UnitRate = {
  unit_kind: string;
  rate_cents: number;
  included_count: number;
  manual_count: number | null;
  label: string | null;
};

type PreviewCount = {
  unit_kind: string;
  measured: number;
  manual_count: number | null;
  counted: number;
  included: number;
  billable: number;
  rate_cents: number;
  amount_cents: number;
  label: string | null;
};

type Preview = {
  counted_at: string;
  monthly_retainer_cents: number;
  counts: PreviewCount[];
  units_total_cents: number;
  total_cents: number;
  stale_after_days: number;
  stale_count: number;
  stale_hostnames: string[];
  unpriced_devices: string[];
};

const KINDS: { value: string; label: string; help: string }[] = [
  {
    value: "endpoint",
    label: "Any managed endpoint",
    help: "Every device with an agent, whatever its role. One rate for all of them.",
  },
  {
    value: "workstation",
    label: "Workstation",
    help: "Devices detected as workstations.",
  },
  { value: "server", label: "Server", help: "Devices detected as servers." },
  {
    value: "network_device",
    label: "Network device",
    help: "Switches, routers, firewalls and printers being monitored.",
  },
  {
    value: "m365_seat",
    label: "Licensed seat",
    help: "Enabled Microsoft 365 accounts holding at least one licence.",
  },
  {
    value: "custom",
    label: "Custom (you set the count)",
    help: "Nothing is measured — needs a count you enter yourself.",
  },
];

const kindLabel = (k: string) => KINDS.find((x) => x.value === k)?.label ?? k;
const money = (cents: number) =>
  (cents / 100).toLocaleString(undefined, { style: "currency", currency: "USD" });

export default function PSAUnitRates({
  contractId,
  contractName,
  onClose,
}: {
  contractId: number;
  contractName: string;
  onClose: () => void;
}) {
  const [rates, setRates] = useState<UnitRate[]>([]);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const loadPreview = useCallback(async () => {
    try {
      const p = await api.get(`/api/v1/psa/contracts/${contractId}/unit-preview`);
      setPreview(p.data);
    } catch {
      setPreview(null);
    }
  }, [contractId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/api/v1/psa/contracts/${contractId}/unit-rates`);
        if (alive) setRates(r.data || []);
        await loadPreview();
      } catch {
        if (alive) setErr("Could not load the rates for this contract.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [contractId, loadPreview]);

  const used = new Set(rates.map((r) => r.unit_kind));
  const available = KINDS.filter((k) => !used.has(k.value));

  // Mirrors the server's refusal. "endpoint" already covers every device, so
  // pricing it alongside workstation/server bills the same machine twice.
  const doubleBilled =
    used.has("endpoint") && (used.has("workstation") || used.has("server"));

  const addRow = (kind: string) =>
    setRates([
      ...rates,
      {
        unit_kind: kind,
        rate_cents: 0,
        included_count: 0,
        manual_count: kind === "custom" ? 0 : null,
        label: null,
      },
    ]);

  const update = (i: number, patch: Partial<UnitRate>) =>
    setRates(rates.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const save = async () => {
    setErr("");
    setSaving(true);
    try {
      await api.put(`/api/v1/psa/contracts/${contractId}/unit-rates`, rates);
      await loadPreview();
      setMsg("Rates saved");
      setTimeout(() => setMsg(""), 2500);
    } catch (e: any) {
      // The server's message is the useful one — it names which rule was hit.
      setErr(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-40 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-3xl my-8">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Per-unit rates
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {contractName} — billed monthly against what this customer actually has.
          </p>
        </div>

        <div className="px-5 py-4 space-y-4">
          {err && (
            <div className="bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-sm px-3 py-2 rounded-lg">
              {err}
            </div>
          )}
          {msg && (
            <div className="bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-sm px-3 py-2 rounded-lg">
              {msg}
            </div>
          )}
          {doubleBilled && (
            <div className="bg-amber-50 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 text-sm px-3 py-2 rounded-lg">
              “Any managed endpoint” already covers workstations and servers — with
              both priced, those machines bill twice. Remove one before saving.
            </div>
          )}

          {loading ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">Loading…</div>
          ) : (
            <>
              {/* Rate rows */}
              <div className="space-y-2">
                {rates.map((r, i) => (
                  <div
                    key={`${r.unit_kind}-${i}`}
                    className="rounded-lg border border-gray-200 dark:border-gray-700 p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {kindLabel(r.unit_kind)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {KINDS.find((k) => k.value === r.unit_kind)?.help}
                        </div>
                      </div>
                      <button
                        onClick={() => setRates(rates.filter((_, idx) => idx !== i))}
                        className="text-xs text-red-600 dark:text-red-400 hover:underline shrink-0"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Rate per month ($)
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={r.rate_cents / 100}
                          onChange={(e) =>
                            update(i, {
                              rate_cents: Math.max(0, Math.round(Number(e.target.value) * 100)),
                            })
                          }
                          className="mt-1 w-full border dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm"
                        />
                      </label>
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        Included free
                        <input
                          type="number"
                          min="0"
                          value={r.included_count}
                          onChange={(e) =>
                            update(i, { included_count: Math.max(0, Number(e.target.value)) })
                          }
                          className="mt-1 w-full border dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm"
                        />
                      </label>
                      <label className="text-xs text-gray-500 dark:text-gray-400">
                        {r.unit_kind === "custom" ? "Count" : "Fixed count (optional)"}
                        <input
                          type="number"
                          min="0"
                          placeholder={r.unit_kind === "custom" ? "required" : "count the fleet"}
                          value={r.manual_count ?? ""}
                          onChange={(e) =>
                            update(i, {
                              manual_count:
                                e.target.value === "" ? null : Math.max(0, Number(e.target.value)),
                            })
                          }
                          className="mt-1 w-full border dark:border-gray-600 rounded-lg px-2 py-1.5 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm"
                        />
                      </label>
                    </div>
                    {r.manual_count !== null && r.unit_kind !== "custom" && (
                      <p className="text-xs text-amber-700 dark:text-amber-400 mt-2">
                        A fixed count overrides the live measurement, and the invoice will
                        say “contracted count” so it does not read as a measurement.
                      </p>
                    )}
                  </div>
                ))}
                {rates.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                    No per-unit rates yet. This contract bills only its base monthly fee.
                  </p>
                )}
              </div>

              {available.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {available.map((k) => (
                    <button
                      key={k.value}
                      onClick={() => addRow(k.value)}
                      className="text-xs border border-gray-300 dark:border-gray-600 dark:text-gray-100 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                    >
                      + {k.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Preview */}
              {preview && (
                <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 p-3">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    What this bills today
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Counted from the fleet as it stands now — saved rates only.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="text-gray-500 dark:text-gray-400">
                        <tr>
                          <th className="text-left py-1">Unit</th>
                          <th className="text-right py-1">Counted</th>
                          <th className="text-right py-1">Free</th>
                          <th className="text-right py-1">Billed</th>
                          <th className="text-right py-1">Rate</th>
                          <th className="text-right py-1">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-900 dark:text-gray-100">
                        {preview.counts.map((c) => (
                          <tr key={c.unit_kind} className="border-t border-gray-100 dark:border-gray-700">
                            <td className="py-1">
                              {kindLabel(c.unit_kind)}
                              {c.manual_count !== null && (
                                <span className="text-amber-700 dark:text-amber-400"> · fixed</span>
                              )}
                            </td>
                            <td className="text-right py-1 font-mono">{c.counted}</td>
                            <td className="text-right py-1 font-mono">{c.included || "—"}</td>
                            <td className="text-right py-1 font-mono">{c.billable}</td>
                            <td className="text-right py-1 font-mono">{money(c.rate_cents)}</td>
                            <td className="text-right py-1 font-mono">{money(c.amount_cents)}</td>
                          </tr>
                        ))}
                        {preview.monthly_retainer_cents > 0 && (
                          <tr className="border-t border-gray-100 dark:border-gray-700">
                            <td className="py-1" colSpan={5}>
                              Base monthly fee
                            </td>
                            <td className="text-right py-1 font-mono">
                              {money(preview.monthly_retainer_cents)}
                            </td>
                          </tr>
                        )}
                        <tr className="border-t-2 border-gray-300 dark:border-gray-600 font-semibold">
                          <td className="py-1.5" colSpan={5}>
                            Monthly total before tax
                          </td>
                          <td className="text-right py-1.5 font-mono">
                            {money(preview.total_cents)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {preview.unpriced_devices.length > 0 && (
                    <div className="mt-3 text-xs text-amber-800 dark:text-amber-300">
                      <strong>
                        {preview.unpriced_devices.length} device
                        {preview.unpriced_devices.length === 1 ? "" : "s"} match no rate above and
                        will not be billed:
                      </strong>{" "}
                      {preview.unpriced_devices.slice(0, 6).join(", ")}
                      {preview.unpriced_devices.length > 6 && " …"}
                      <div className="mt-0.5">
                        Add an “Any managed endpoint” rate, or set those devices’ role.
                      </div>
                    </div>
                  )}
                  {preview.stale_count > 0 && (
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                      {preview.stale_count} device
                      {preview.stale_count === 1 ? " has" : "s have"} not checked in for{" "}
                      {preview.stale_after_days}+ days. They <strong>are</strong> billed — a quiet
                      machine is often still managed — but worth a look:{" "}
                      {preview.stale_hostnames.slice(0, 6).join(", ")}
                      {preview.stale_hostnames.length > 6 && " …"}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/40"
          >
            Close
          </button>
          <button
            onClick={save}
            disabled={saving || loading || doubleBilled}
            className="px-4 py-1.5 text-sm rounded-lg bg-brand text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save rates"}
          </button>
        </div>
      </div>
    </div>
  );
}
